import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { HttpEventType } from '@angular/common/http';

import { validateTracsExport } from 'src/app/_utils/validate-tracs';
import {
  dropDuplicateContents,
  MAX_IMPORT_FILE_BYTES,
  MediaImport,
  MediaImportService,
  plural,
  runPool,
} from 'src/app/_services/media-import.service';

/** as invenire-v2: more at once and the uploads start dropping */
const UPLOADS_AT_ONCE = 4;

/** A picked export and how its upload went */
interface PickedFile {
  file: File;
  status: 'ready' | 'uploading' | 'done' | 'failed';
  percent?: number;
  reason?: string;
  /** a network error or a 5xx, which another try may get past (a 4xx won't) */
  retryable?: boolean;
}

/** Drop or browse several exports and upload them, as invenire-v2's upload form */
@Component({
    selector: 'app-import-upload',
    templateUrl: './import-upload.component.html',
    styleUrls: ['./import-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImportUploadComponent {
  @Input() projectId: string;
  /** each import an upload queued */
  @Output() uploaded = new EventEmitter<MediaImport>();

  files: PickedFile[] = [];
  /** what was skipped from the last files picked */
  notices: string[] = [];
  uploading = false;
  dropActive = false;
  readonly plural = plural;
  private dragDepth = 0;

  constructor(private mediaImportService: MediaImportService) { }

  /** the files the Upload (or Retry) button sends */
  get pending() {
    return this.files.filter(f => f.status === 'ready' || (f.status === 'failed' && f.retryable));
  }

  get retrying() {
    return this.pending.length > 0 && this.pending.every(f => f.status === 'failed');
  }

  browse(input: HTMLInputElement) {
    const picked = Array.from(input.files ?? []);
    input.value = '';
    this.add(picked);
  }

  dragEnter(event: DragEvent) {
    event.preventDefault();
    this.dragDepth++;
    this.dropActive = true;
  }

  // entering a child leaves the zone first, so only the last leave counts
  dragLeave() {
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    this.dropActive = this.dragDepth > 0;
  }

  drop(event: DragEvent) {
    event.preventDefault();
    this.dragDepth = 0;
    this.dropActive = false;
    this.add(Array.from(event.dataTransfer?.files ?? []));
  }

  /** Add the .json files picked, each contents once */
  async add(picked: File[]) {
    this.notices = [];
    const exports = picked.filter(file => /\.json$/i.test(file.name));
    if(exports.length < picked.length) {
      this.notices.push(`Skipped ${plural(picked.length - exports.length, 'non-JSON file')}`);
    }
    try {
      const { added, skipped } = await dropDuplicateContents(this.files.map(f => f.file), exports);
      skipped.forEach(({ file, sameAs }) => this.notices.push(`Skipped ${file.name}: same contents as ${sameAs.name}`));
      this.files.push(...added.map(file => ({ file, status: 'ready' as const })));
    } catch {
      this.notices.push('Could not read the selected files');
    }
  }

  remove(entry: PickedFile) {
    this.files = this.files.filter(f => f !== entry);
  }

  clear() {
    this.files = [];
    this.notices = [];
  }

  async upload() {
    this.uploading = true;
    await runPool(this.pending, UPLOADS_AT_ONCE, entry => this.send(entry));
    this.uploading = false;
  }

  /**
   * Upload one export: checked here with the Lambda's rules first (as invenire-v2's upload checks
   * it), then PUT to the URL the API hands out; the S3 trigger hands it to the Lambda, and its row
   * shows how the import goes
   */
  private async send(entry: PickedFile) {
    const { file } = entry;
    const fail = (reason: string, retryable = false) => Object.assign(entry, { status: 'failed', reason, retryable });
    const retryable = (status: number) => status === 0 || status >= 500;

    if(file.size > MAX_IMPORT_FILE_BYTES) {
      return fail(`File size exceeds the 50 MB limit (received ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
    }
    Object.assign(entry, { status: 'uploading', percent: 0, reason: undefined });
    let sha256: string;
    try {
      const { problems, problemCount } = validateTracsExport(await file.text());
      if(problemCount > 0) {
        const more = problemCount - 1;
        return fail(`Not a valid JSON media export. ${problems[0]}${more ? ` (and ${more} more ${more === 1 ? 'problem' : 'problems'})` : ''}`);
      }
      sha256 = await this.mediaImportService.hash(file);
    } catch {
      return fail('The file could not be read');
    }

    return new Promise<void>(done => {
      this.mediaImportService.createUpload(this.projectId, { fileName: file.name, size: file.size, sha256 }).subscribe({
        next: ({ import: row, upload }) => this.mediaImportService.putFile(upload.url, file, upload.headers).subscribe({
          next: event => {
            if(event.type === HttpEventType.UploadProgress && event.total) {
              entry.percent = Math.round(100 * event.loaded / event.total);
            }
            if(event.type === HttpEventType.Response) {
              Object.assign(entry, { status: 'done', percent: 100 });
              this.uploaded.emit(row);
            }
          },
          error: ({ status }) => {
            // nothing arrived: drop the queued import so it doesn't wait for the file forever
            this.mediaImportService.act(this.projectId, row.id, 'cancel').subscribe({ error: () => undefined });
            fail('The file could not be uploaded', retryable(status));
            done();
          },
          complete: done,
        }),
        error: ({ status, error }) => {
          fail(error?.message || 'Something went wrong!', retryable(status));
          done();
        },
      });
    });
  }
}
